'use client'

import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { SongArtwork } from '@/components/ui/song-artwork'
import { Music, Play, CheckCircle, History, Clock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  InlineDialog,
  InlineDialogHeader,
  InlineDialogBody,
  InlineDialogTitle,
  InlineDialogDescription,
} from './inline-dialog'
import { useTranslations } from 'next-intl'
import type { DemoNowPlaying, DemoPlayedSong, DemoSong } from '../demo-types'

export interface NowPlayingControlsProps {
  nowPlaying: DemoNowPlaying | null
  lastPlayed: DemoPlayedSong | null
  playedSongs: DemoPlayedSong[]
  isSongHistoryOpen: boolean
  onSetSongHistoryOpen: (open: boolean) => void
  onEndCurrentSong: () => void
  onReplay: (song: DemoSong) => void
}

export function NowPlayingControls({
  nowPlaying,
  lastPlayed,
  playedSongs,
  isSongHistoryOpen,
  onSetSongHistoryOpen,
  onEndCurrentSong,
  onReplay,
}: NowPlayingControlsProps) {
  const t = useTranslations('demo')
  return (
    <>
      <div className="space-y-2">
        {/* {t('nowPlayingArtist')} */}
        <div className="flex items-center gap-3 p-3 rounded-lg border border-zinc-800 bg-zinc-900/50">
          {nowPlaying ? (
            <>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="relative shrink-0">
                  <SongArtwork src={nowPlaying.song.artworkUrl} size={56} className="h-12 w-12 sm:h-14 sm:w-14 rounded-lg" />
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                    <Play className="h-2.5 w-2.5 text-white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-green-400 uppercase font-medium">{t('nowPlayingArtist')}</p>
                  <p className="text-sm sm:text-base font-semibold text-white truncate">{nowPlaying.song.title}</p>
                  <p className="text-xs sm:text-sm text-zinc-400 truncate">{nowPlaying.song.artist}</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-zinc-700 shrink-0"
                onClick={onEndCurrentSong}
              >
                <CheckCircle className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('done')}</span>
              </Button>
            </>
          ) : (
            <div className="flex items-center gap-3 w-full">
              <div className="h-12 w-12 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0">
                <Music className="h-6 w-6 text-zinc-600" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] text-zinc-500 uppercase">{t('nowPlayingArtist')}</p>
                <p className="text-sm text-zinc-400">{t('selectSongToStart')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Previous Track */}
        {lastPlayed && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-800 bg-zinc-900/30">
            <button
              onClick={() => onSetSongHistoryOpen(true)}
              className="flex items-center gap-2 flex-1 min-w-0 text-left group hover:opacity-80 transition-opacity cursor-pointer"
            >
              <History className="h-3.5 w-3.5 text-zinc-600 shrink-0" />
              <p className="text-[10px] text-zinc-600 uppercase shrink-0">{t('previous')}</p>
              <SongArtwork src={lastPlayed.song.artworkUrl} size={32} className="h-8 w-8 rounded opacity-50 grayscale" />
              <p className="text-xs text-zinc-500 truncate">{lastPlayed.song.title}</p>
            </button>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onReplay(lastPlayed.song)}
                  className="h-7 w-7 p-0 text-zinc-500 hover:text-white hover:bg-zinc-700"
                >
                  <Play className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('replayThisSong')}</TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>

      {/* Song History Modal */}
      <InlineDialog open={isSongHistoryOpen} onOpenChange={onSetSongHistoryOpen} className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <InlineDialogHeader onClose={() => onSetSongHistoryOpen(false)}>
          <InlineDialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-violet-400" />
            {t('songHistoryArtist')}
          </InlineDialogTitle>
          <InlineDialogDescription>
            {t('songsPerformed')}
          </InlineDialogDescription>
        </InlineDialogHeader>
        <InlineDialogBody className="flex-1 overflow-y-auto">
            {playedSongs.length > 0 ? (
              <div className="space-y-2 py-2">
                {playedSongs.map((item, index) => (
                  <div key={item.id} className="flex items-center gap-3 p-2 rounded-lg bg-zinc-800/50 group">
                    <span className="text-xs font-medium text-zinc-600 w-5 text-center shrink-0">
                      {playedSongs.length - index}
                    </span>
                    <SongArtwork src={item.song.artworkUrl} size={40} className="h-10 w-10 rounded shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{item.song.title}</p>
                      <p className="text-xs text-zinc-400 truncate">{item.song.artist}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {item.wasRequested && (
                          <Badge variant="outline" className="text-[10px] border-violet-500/30 text-violet-400 px-1 py-0">{t('requested')}</Badge>
                        )}
                        <span className="text-[10px] text-zinc-600 flex items-center gap-1">
                          <Clock className="h-2.5 w-2.5" />
                          {new Date(item.playedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { onReplay(item.song); onSetSongHistoryOpen(false) }}
                      className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-white"
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-400">
                <Music className="h-10 w-10 mx-auto mb-2 text-zinc-600" />
                <p className="text-sm">{t('noSongsPlayedArtist')}</p>
              </div>
            )}
        </InlineDialogBody>
      </InlineDialog>
    </>
  )
}
